import { useEffect, useMemo, useState } from "react";
import { Navigate, useLocation, useSearchParams } from "react-router-dom";
import api from "../api/axios";
import AdminLoadingOverlay from "../components/AdminLoadingOverlay";
import AdminShell from "../components/AdminShell";
import { useAuth } from "../context/AuthContext";
import { getUserDisplayName } from "../utils/userDisplayName";
import {
  COUNTRY_CODE_OPTIONS,
  combineInternationalContact,
  getContactNumberError,
  getCountryOptionLabel,
  getMaxLocalPhoneDigits,
  normalizeLocalPhoneNumberForCountry,
} from "../utils/phoneValidation";
import "../styles/admin.css";

const initialCreateForm = {
  firstName: "",
  lastName: "",
  email: "",
  contactCountryCode: "+63",
  contactNumber: "",
  role: "staff",
  isActive: true,
};

const initialEditForm = {
  name: "",
  username: "",
  email: "",
  role: "staff",
};

const ADMIN_ROLE_OPTIONS = [
  { value: "technical_admin", label: "Technical Admin" },
  { value: "admin", label: "Admin" },
  { value: "staff", label: "Staff" },
];
const VERIFICATION_EXPIRY_WARNING_DAYS = 30;

function getRoleLabel(role) {
  if (role === "technical_admin") return "Technical Admin";
  if (role === "admin") return "Admin";
  if (role === "staff") return "Staff";
  return "Customer";
}

function isTechnicalAdminAccount(account) {
  return account?.role === "technical_admin";
}

function isAdminPortalAccount(account) {
  return ["technical_admin", "admin", "staff"].includes(account?.role);
}

function isCustomerAccount(account) {
  return String(account?.role || "").toLowerCase() === "user";
}

function EditIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4 17.2V20h2.8L17.1 9.7l-2.8-2.8L4 17.2Zm15.7-10.1c.4-.4.4-1 0-1.4l-1.4-1.4a1 1 0 0 0-1.4 0l-1.1 1.1 2.8 2.8 1.1-1.1Z" />
    </svg>
  );
}

function MoreIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 8a2 2 0 1 0 0-4 2 2 0 0 0 0 4Zm0 6a2 2 0 1 0 0-4 2 2 0 0 0 0 4Zm0 6a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z" />
    </svg>
  );
}

const VERIFICATION_FILTER_OPTIONS = [
  { value: "all", label: "All verifications" },
  { value: "pending_review", label: "Pending Review" },
  { value: "expiring", label: "Expiring soon" },
];

const VALID_VERIFICATION_FILTERS = new Set(
  VERIFICATION_FILTER_OPTIONS.map((o) => o.value)
);

const REQUEST_UPDATE_REASONS = [
  { value: "expiring_soon", label: "Expiring soon", soft: true },
  { value: "expired_document", label: "Expired document", soft: false },
  { value: "needs_clearer_copy", label: "Needs clearer copy", soft: false },
  { value: "wrong_document", label: "Wrong document type", soft: false },
  { value: "face_mismatch", label: "Document does not match face photo", soft: false },
  { value: "updated_info", label: "Updated customer information", soft: false },
  { value: "other", label: "Other", soft: false },
];

export default function Users() {
  const { user, setUser } = useAuth();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const initialVerificationFilter = (() => {
    const fromQuery = String(searchParams.get("tab") || "").trim().toLowerCase();
    if (VALID_VERIFICATION_FILTERS.has(fromQuery)) return fromQuery;
    return "pending_review";
  })();

  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [verificationFilter, setVerificationFilter] = useState(initialVerificationFilter);
  const [msg, setMsg] = useState("");

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  const [createForm, setCreateForm] = useState(initialCreateForm);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState(initialEditForm);

  const [createErrors, setCreateErrors] = useState({});
  const [editErrors, setEditErrors] = useState({});
  const [menuOpenId, setMenuOpenId] = useState(null);
  const [selectedVerificationUser, setSelectedVerificationUser] = useState(null);
  const [verificationRemarks, setVerificationRemarks] = useState("");
  const [verificationError, setVerificationError] = useState("");
  const [verificationExpiryForm, setVerificationExpiryForm] = useState({});
  const [requestUpdateModal, setRequestUpdateModal] = useState(null);
  const [requestUpdateReason, setRequestUpdateReason] = useState("");
  const [requestUpdateRemarks, setRequestUpdateRemarks] = useState("");
  const [requestUpdateError, setRequestUpdateError] = useState("");
  const [documentPreview, setDocumentPreview] = useState(null);
  const [deactivateModal, setDeactivateModal] = useState(null); // { user, reason, error, submitting }
  const [statusConfirm, setStatusConfirm] = useState(null);
  const [statusActionLoading, setStatusActionLoading] = useState("");
  const [verificationConfirm, setVerificationConfirm] = useState(null);
  const [verificationActionLoading, setVerificationActionLoading] = useState("");
  const [requestUpdateSubmitting, setRequestUpdateSubmitting] = useState(false);

  const query = useMemo(() => ({ search, status }), [search, status]);
  const editingUser = useMemo(
    () => users.find((item) => item._id === editId) || null,
    [editId, users]
  );
  const editingAdminAccount = ADMIN_ROLE_OPTIONS.some(
    (option) => option.value === editingUser?.role
  );
  const editingOwnAccount = [user?.id, user?._id]
    .filter(Boolean)
    .some((id) => String(id) === String(editId));

  const isTechnicalAdmin = user?.role === "technical_admin";
  const isAdmin = isTechnicalAdmin || user?.role === "admin";
  const isStaff = user?.role === "staff";
  const canUseUsersModule = isAdmin || isStaff;

  const loadUsers = async () => {
    setMsg("");
    try {
      const res = await api.get("/users", { params: query });
      setUsers(res.data.users || []);
    } catch (err) {
      setMsg(err?.response?.data?.message || "Failed to load accounts.");
    }
  };

  useEffect(() => {
    if (canUseUsersModule) {
      loadUsers();
    }
    // eslint-disable-next-line
  }, [query.search, query.status, canUseUsersModule]);

  useEffect(() => {
    if (location.state?.verificationFilter) {
      setVerificationFilter(location.state.verificationFilter);
    }
  }, [location.state?.verificationFilter]);

  useEffect(() => {
    const handleOutsideClick = () => setMenuOpenId(null);
    window.addEventListener("click", handleOutsideClick);
    return () => window.removeEventListener("click", handleOutsideClick);
  }, []);

  const validateName = (value, label = "Name") => {
    const clean = value.trim();
    if (!clean) return `${label} is required.`;
    if (clean.length < 2) return `${label} must be at least 2 characters.`;
    return "";
  };

  const validateEmail = (value) => {
    const clean = value.trim().toLowerCase();
    if (!clean) return "Email is required.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clean)) {
      return "Enter a valid email address.";
    }
    return "";
  };

  const validateUsername = (value) => {
    const clean = String(value || "").trim().toLowerCase();
    if (!clean) return "Username is required.";
    if (
      clean.length < 3 ||
      clean.length > 20 ||
      !/^[a-z0-9_.]+$/.test(clean) ||
      /^[._]|[._]$/.test(clean)
    ) {
      return "Username must be 3 to 20 characters and may contain letters, numbers, dots, or underscores.";
    }
    return "";
  };

  const validateCreateForm = () => {
    const nextErrors = {
      firstName: validateName(createForm.firstName, "First name"),
      lastName: validateName(createForm.lastName, "Last name"),
      email: validateEmail(createForm.email),
      contactNumber: getContactNumberError(
        createForm.contactCountryCode,
        createForm.contactNumber
      ),
    };

    Object.keys(nextErrors).forEach((key) => {
      if (!nextErrors[key]) delete nextErrors[key];
    });

    setCreateErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const validateEditForm = () => {
    const nextErrors = {
      name: validateName(editForm.name),
      email: validateEmail(editForm.email),
      username: editingOwnAccount ? validateUsername(editForm.username) : "",
    };

    Object.keys(nextErrors).forEach((key) => {
      if (!nextErrors[key]) delete nextErrors[key];
    });

    setEditErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const openCreateModal = () => {
    setCreateForm(initialCreateForm);
    setCreateErrors({});
    setShowCreateModal(true);
  };

  const closeCreateModal = () => {
    setShowCreateModal(false);
    setCreateErrors({});
  };

  const updateCreateContactCountry = (countryCode) => {
    setCreateForm((prev) => ({
      ...prev,
      contactCountryCode: countryCode,
      contactNumber: normalizeLocalPhoneNumberForCountry(prev.contactNumber, countryCode),
    }));
    setCreateErrors((prev) => ({ ...prev, contactNumber: "" }));
  };

  const updateCreateContactNumber = (value) => {
    setCreateForm((prev) => ({
      ...prev,
      contactNumber: normalizeLocalPhoneNumberForCountry(value, prev.contactCountryCode),
    }));
    setCreateErrors((prev) => ({ ...prev, contactNumber: "" }));
  };

  const createUser = async (e) => {
    e.preventDefault();
    setMsg("");

    if (!validateCreateForm()) {
      setMsg("Please fix the highlighted fields.");
      return;
    }

    try {
      await api.post("/users", {
        firstName: createForm.firstName.trim(),
        lastName: createForm.lastName.trim(),
        email: createForm.email.trim().toLowerCase(),
        contactNumber: combineInternationalContact(
          createForm.contactCountryCode,
          createForm.contactNumber
        ),
        contactCountryCode: createForm.contactCountryCode,
        role: createForm.role,
        isActive: createForm.isActive,
      });

      setCreateForm(initialCreateForm);
      setCreateErrors({});
      setShowCreateModal(false);
      await loadUsers();
      setMsg("Admin account created successfully. Login credentials sent via email.");
    } catch (err) {
      if (
        err?.response?.status === 409 &&
        err?.response?.data?.message === "Email already exists."
      ) {
        setCreateErrors((prev) => ({
          ...prev,
          email: "An account with this email already exists.",
        }));
        return;
      }

      setMsg(err?.response?.data?.message || "Failed to create admin account.");
    }
  };

  const deactivate = (u) => {
    setDeactivateModal({ user: u, reason: "", error: "", submitting: false });
    setMenuOpenId(null);
  };

  const activate = (id) => {
    setStatusConfirm({
      id,
      title: "Reactivate account?",
      message: "This account will regain access to its allowed FleetX portal.",
      confirmLabel: "Reactivate",
      loadingLabel: "Reactivating...",
      tone: "",
      request: () => api.patch(`/users/${id}/activate`),
      successMessage: "Account reactivated successfully.",
      failureMessage: "Failed to reactivate account.",
    });
  };

  const confirmStatusAction = async () => {
    if (!statusConfirm || statusActionLoading) return;
    setMsg("");
    setStatusConfirm(null);
    setStatusActionLoading(String(statusConfirm.id));
    try {
      await statusConfirm.request();
      await loadUsers();
      setMsg(statusConfirm.successMessage);
    } catch (err) {
      setMsg(err?.response?.data?.message || statusConfirm.failureMessage);
    } finally {
      setStatusActionLoading("");
    }
  };

  const openEditModal = (u) => {
    setEditId(u._id);
    setEditForm({
      name: getUserDisplayName(u, ""),
      username: u.username || "",
      email: u.email || "",
      role: ADMIN_ROLE_OPTIONS.some((option) => option.value === u.role)
        ? u.role
        : "staff",
    });
    setEditErrors({});
    setShowEditModal(true);
    setMenuOpenId(null);
  };

  const closeEditModal = () => {
    setEditId(null);
    setEditForm(initialEditForm);
    setEditErrors({});
    setShowEditModal(false);
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    setMsg("");

    if (!validateEditForm()) {
      setMsg("Please fix the highlighted edit fields.");
      return;
    }

    try {
      const payload = {
        name: editForm.name.trim(),
      };

      if (editingOwnAccount) {
        payload.username = editForm.username.trim().toLowerCase();
      }

      if (!editingOwnAccount) {
        payload.email = editForm.email.trim().toLowerCase();
      }

      if (editingAdminAccount && !editingOwnAccount) {
        payload.role = editForm.role;
      }

      const res = await api.put(`/users/${editId}`, payload);
      const updatedUser = res?.data?.user;

      if (
        updatedUser &&
        [user?.id, user?._id].filter(Boolean).some((id) => String(id) === String(editId))
      ) {
        const nextUser = { ...user, ...updatedUser, id: updatedUser.id || updatedUser._id || user.id };
        localStorage.setItem("user", JSON.stringify(nextUser));
        setUser(nextUser);
      }

      closeEditModal();
      await loadUsers();
      setMsg("Account updated successfully.");
    } catch (err) {
      setMsg(err?.response?.data?.message || "Failed to update account.");
    }
  };

  const confirmDeactivate = async () => {
    if (!deactivateModal?.user?._id || deactivateModal.submitting) return;
    const reason = String(deactivateModal.reason || "").trim();
    if (!reason) {
      setDeactivateModal((prev) => ({ ...prev, error: "Please enter a reason for deactivation." }));
      return;
    }
    setMsg("");
    setDeactivateModal((prev) => ({ ...prev, error: "", submitting: true }));
    try {
      await api.patch(`/users/${deactivateModal.user._id}/deactivate`, { reason });
      setDeactivateModal(null);
      await loadUsers();
      setMsg("Account deactivated successfully.");
    } catch (err) {
      setDeactivateModal((prev) => ({
        ...prev,
        submitting: false,
        error: err?.response?.data?.message || "Failed to deactivate account.",
      }));
    }
  };

  const getStatusBadge = (active) =>
    active ? "badge badge-approved" : "badge badge-rejected";

  const getVerificationStatus = (u) => {
    if (!isCustomerAccount(u)) return "Not applicable";

    const verification = u?.verification;
    if (!verification) return "get verified";
    const normalized = normalizeVerificationForUi(verification);
    const validIdStatus = String(normalized.validIdStatus || "").toLowerCase();
    const driverLicenseStatus = String(
      normalized.driverLicenseStatus || normalized.licenseStatus || ""
    ).toLowerCase();
    const validIdUnderReview =
      validIdStatus === "pending_review" || validIdStatus === "pending";
    const licenseUnderReview =
      driverLicenseStatus === "pending_review" || driverLicenseStatus === "pending";
    const expiryInfo = getExpiryInfo(normalized);

    if (normalized.overallStatus === "under_review") return "under review";
    if (expiryInfo.status === "expired") return "expired";
    if (expiryInfo.status === "expiring_soon") return "expiring soon";
    if (normalized.overallStatus === "needs_update") return "needs update";
    if (normalized.overallStatus === "rejected") return "rejected";
    if (normalized.overallStatus === "fully_verified" && validIdUnderReview) {
      return "fully verified - valid id under review";
    }
    if (normalized.overallStatus === "fully_verified" && licenseUnderReview) {
      return "fully verified - license under review";
    }
    if (normalized.overallStatus === "fully_verified") return "fully verified";
    if (
      normalized.overallStatus === "basic_verified" &&
      licenseUnderReview
    ) {
      return "basic verified - license under review";
    }
    if (normalized.overallStatus === "basic_verified") return "basic verified";
    return String(normalized.overallStatus || "get verified").toLowerCase();
  };

  const getVerificationBadge = (u) => {
    if (!isCustomerAccount(u)) return "badge";

    const value = getVerificationStatus(u);
    if (value.includes("under review")) return "badge badge-pending";
    if (value === "expiring soon") return "badge badge-pending";
    if (value === "expired") return "badge badge-rejected";
    if (value === "needs update") return "badge badge-rejected";
    if (value === "fully verified") return "badge badge-approved";
    if (value === "basic verified") return "badge badge-approved";
    if (value === "unverified" || value === "rejected") return "badge badge-rejected";
    return "badge badge-pending";
  };

  const isReviewStatus = (statusValue) =>
    ["pending", "pending_review", "under_review"].includes(
      String(statusValue || "").toLowerCase()
    );

  const getDocumentBadge = (statusValue) => {
    const value = String(statusValue || "").toLowerCase();
    if (value === "complete") return "badge badge-approved";
    if (value === "approved") return "badge badge-approved";
    if (value === "rejected") return "badge badge-rejected";
    if (isReviewStatus(value)) return "badge badge-pending";
    return "badge";
  };

  const formatDocumentStatus = (statusValue) => {
    const value = String(statusValue || "none").toLowerCase();
    if (isReviewStatus(value)) return "Pending Review";
    if (value === "not_required") return "Not Required";
    if (value === "approved") return "Approved";
    if (value === "rejected") return "Rejected";
    return "Missing";
  };

  const formatVerificationDate = (value) => {
    if (!value) return "Not available";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "Not available";
    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const formatDateInputValue = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return date.toISOString().slice(0, 10);
  };

  const getExpiryInfo = (verification = {}) => {
    const rawDate =
      verification.verificationExpiresAt ||
      verification.licenseExpiryDate ||
      verification.idExpiryDate ||
      "";
    const storedStatus = String(verification.verificationExpiryStatus || "").toLowerCase();

    if (storedStatus === "no_expiry" || verification.idHasNoExpiry || verification.licenseHasNoExpiry) {
      return {
        status: "no_expiry",
        label: "No Expiry",
        tone: "approved",
        date: null,
        daysRemaining: null,
      };
    }

    if (!rawDate) {
      return {
        status: "unknown",
        label: "Not Set",
        tone: "muted",
        date: null,
        daysRemaining: null,
      };
    }

    const expiryDate = new Date(rawDate);
    if (Number.isNaN(expiryDate.getTime())) {
      return {
        status: "unknown",
        label: "Not Set",
        tone: "muted",
        date: null,
        daysRemaining: null,
      };
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const expiryDay = new Date(expiryDate);
    expiryDay.setHours(0, 0, 0, 0);
    const daysRemaining = Math.ceil(
      (expiryDay.getTime() - today.getTime()) / (24 * 60 * 60 * 1000)
    );
    const statusValue =
      daysRemaining < 0
        ? "expired"
        : daysRemaining <= VERIFICATION_EXPIRY_WARNING_DAYS
        ? "expiring_soon"
        : "valid";

    return {
      status: statusValue,
      label:
        statusValue === "expired"
          ? "Expired"
          : statusValue === "expiring_soon"
          ? "Expiring Soon"
          : "Valid",
      tone:
        statusValue === "expired"
          ? "rejected"
          : statusValue === "expiring_soon"
          ? "pending"
          : "approved",
      date: expiryDate,
      daysRemaining,
    };
  };

  const getVerificationTypeLabel = (userRecord = {}, verification = {}) => {
    const type = String(
      verification.verificationType || userRecord.verificationType || ""
    ).toLowerCase();
    if (["drivers_license", "driver_license", "self_drive"].includes(type)) {
      return "Self-Drive";
    }
    if (["government_id", "valid_id", "with_driver"].includes(type)) {
      return "With Driver";
    }
    if (verification.licenseFront || verification.licenseFrontImage) return "Self-Drive";
    if (verification.validIdFront || verification.validIdFrontImage) return "With Driver";
    return "Not specified";
  };

  const getLegacyFaceDocumentId = (verification = {}) => {
    const verificationType = String(verification.verificationType || "").toLowerCase();
    const overallStatus = String(
      verification.overallVerificationStatus ||
        verification.overallStatus ||
        verification.status ||
        ""
    ).toLowerCase();
    const hasValidIdImages = Boolean(
      (verification.validIdFrontImage ||
        verification.validIdFront ||
        verification.idFrontImage ||
        verification.validIdImage) &&
        (verification.validIdBackImage || verification.validIdBack)
    );
    const hasLicenseImages = Boolean(
      (verification.licenseFrontImage ||
        verification.licenseFront ||
        verification.driverLicenseFrontImage ||
        verification.driverLicenseImage) &&
        (verification.licenseBackImage ||
          verification.licenseBack ||
          verification.driverLicenseBackImage)
    );

    if (["drivers_license", "driver_license", "self_drive"].includes(verificationType)) {
      return "license";
    }
    if (["government_id", "valid_id", "with_driver"].includes(verificationType)) {
      return "validId";
    }
    if (verification.verificationLevel === "full" || overallStatus === "fully_verified") {
      return "license";
    }
    if (verification.verificationLevel === "basic" || overallStatus === "basic_verified") {
      return "validId";
    }
    if (hasLicenseImages && !hasValidIdImages) return "license";
    if (hasValidIdImages && !hasLicenseImages) return "validId";
    return "";
  };

  const getVerificationImages = (verification = {}) => {
    const legacyFace =
      verification.facePhotoUrl ||
      verification.faceVerificationPhotoUrl ||
      verification.facePhoto ||
      "";
    const legacyFaceDocumentId = getLegacyFaceDocumentId(verification);
    const validIdSelfie =
      verification.validIdSelfieUrl ||
      verification.validIdFacePhotoUrl ||
      (legacyFaceDocumentId === "validId" ? legacyFace : "");
    const licenseSelfie =
      verification.licenseSelfieUrl ||
      verification.driverLicenseSelfieUrl ||
      (legacyFaceDocumentId === "license" ? legacyFace : "");

    return {
      validIdFront:
        verification.validIdFrontImage ||
        verification.validIdFront ||
        verification.idFrontImage ||
        verification.validIdImage ||
        "",
      validIdBack:
        verification.validIdBackImage ||
        verification.validIdBack ||
        "",
      licenseFront:
        verification.licenseFrontImage ||
        verification.licenseFront ||
        verification.driverLicenseFrontImage ||
        verification.driverLicenseImage ||
        "",
      licenseBack:
        verification.licenseBackImage ||
        verification.licenseBack ||
        verification.driverLicenseBackImage ||
        "",
      facePhotoUrl: legacyFace,
      validIdSelfieUrl: validIdSelfie,
      licenseSelfieUrl: licenseSelfie,
    };
  };

  const hasFacePhotoForReview = (verification = {}, documentKey = "") => {
    const images = getVerificationImages(verification);
    const status = String(
      verification.overallVerificationStatus ||
        verification.overallStatus ||
        verification.status ||
        ""
    ).toLowerCase();
    const image =
      documentKey === "driverLicense"
        ? images.licenseSelfieUrl
        : documentKey === "validId"
        ? images.validIdSelfieUrl
        : images.validIdSelfieUrl || images.licenseSelfieUrl || images.facePhotoUrl;
    return Boolean(image) || ["approved", "basic_verified", "fully_verified"].includes(status);
  };

  const hasValidIdDocument = (verification = {}) => {
    const images = getVerificationImages(verification);
    return Boolean(images.validIdFront && images.validIdBack && hasFacePhotoForReview(verification, "validId"));
  };

  const hasLicenseDocument = (verification = {}) => {
    const images = getVerificationImages(verification);
    return Boolean(images.licenseFront && images.licenseBack && hasFacePhotoForReview(verification, "driverLicense"));
  };

  const getDocumentStatusForUi = (verification = {}, documentKey) => {
    const statusKey =
      documentKey === "validId" ? "validIdStatus" : "driverLicenseStatus";
    const hasCompleteDocument =
      documentKey === "validId"
        ? hasValidIdDocument(verification)
        : hasLicenseDocument(verification);
    const statusValue = String(verification?.[statusKey] || "none").toLowerCase();

    if (statusValue === "not_required") return "not_required";
    if (!hasCompleteDocument) return "none";
    if (statusValue === "missing" || statusValue === "none") return "pending_review";
    if (isReviewStatus(statusValue)) return "pending_review";
    return statusValue;
  };

  const needsVerificationReview = (u) => {
    if (!isCustomerAccount(u)) return false;

    const verification = u?.verification;
    if (!verification) return false;
    const normalized = normalizeVerificationForUi(verification);
    const validIdStatus = getDocumentStatusForUi(normalized, "validId");
    const driverLicenseStatus = getDocumentStatusForUi(normalized, "driverLicense");
    return [validIdStatus, driverLicenseStatus].some((value) =>
      isReviewStatus(value)
    );
  };

  const hasViewableVerificationDocuments = (u) => {
    if (!isCustomerAccount(u)) return false;

    const verification = u?.verification;
    if (!verification) return false;

    const normalized = normalizeVerificationForUi(verification);
    const images = getVerificationImages(normalized);
    const status = String(
      normalized.overallVerificationStatus ||
        normalized.overallStatus ||
        normalized.status ||
        ""
    ).toLowerCase();

    return Boolean(
      images.validIdFront ||
        images.validIdBack ||
        images.licenseFront ||
        images.licenseBack ||
        images.validIdSelfieUrl ||
        images.licenseSelfieUrl ||
        images.facePhotoUrl ||
        ["under_review", "basic_verified", "fully_verified", "rejected", "needs_update", "approved", "pending"].includes(status)
    );
  };

  const formatReviewerName = (reviewer) => {
    if (!reviewer) return "Not available";
    if (typeof reviewer === "string") return reviewer;
    return (
      reviewer.name ||
      [reviewer.firstName, reviewer.lastName].filter(Boolean).join(" ") ||
      reviewer.email ||
      "Not available"
    );
  };

  const normalizeVerificationForUi = (verification, statusOverride = null) => {
    if (!verification) return null;
    const rawStatus = String(
      statusOverride ||
        verification.overallVerificationStatus ||
        verification.status ||
        verification.overallStatus ||
        "unverified"
    ).toLowerCase();
    const images = getVerificationImages(verification);
    const hasValidId = Boolean(images.validIdFront && images.validIdBack);
    const hasDriverLicense = Boolean(images.licenseFront && images.licenseBack);
    const validIdStatus =
      hasValidId &&
      (!verification.validIdStatus || verification.validIdStatus === "missing")
        ? rawStatus
        : verification.validIdStatus || "none";
    const driverLicenseStatus =
      hasDriverLicense &&
      (!verification.driverLicenseStatus ||
        verification.driverLicenseStatus === "missing")
        ? rawStatus
        : verification.driverLicenseStatus || verification.licenseStatus || "none";
    const computedLevel =
      driverLicenseStatus === "approved"
        ? "full"
        : validIdStatus === "approved"
        ? "basic"
        : "none";
    const verificationLevel =
      computedLevel !== "none"
        ? computedLevel
        : verification.verificationLevel || "none";
    const overallStatus =
      driverLicenseStatus === "approved"
        ? "fully_verified"
        : validIdStatus === "approved"
        ? "basic_verified"
        : validIdStatus === "pending_review" ||
          validIdStatus === "pending" ||
          validIdStatus === "under_review" ||
          driverLicenseStatus === "pending_review" ||
          driverLicenseStatus === "pending" ||
          driverLicenseStatus === "under_review"
        ? "under_review"
        : rawStatus === "needs_update"
        ? "needs_update"
        : rawStatus === "rejected"
        ? "rejected"
        : validIdStatus === "rejected" || driverLicenseStatus === "rejected"
        ? "unverified"
        : rawStatus === "approved"
        ? verificationLevel === "full"
          ? "fully_verified"
          : "basic_verified"
        : rawStatus === "pending"
        ? "under_review"
        : verification.overallVerificationStatus || verification.overallStatus || rawStatus;

    return {
      ...verification,
      validIdFrontImage: images.validIdFront,
      validIdBackImage: images.validIdBack,
      licenseFrontImage: images.licenseFront,
      licenseBackImage: images.licenseBack,
      facePhotoUrl: images.facePhotoUrl,
      faceVerificationPhotoUrl: images.facePhotoUrl,
      validIdSelfieUrl: images.validIdSelfieUrl,
      validIdFacePhotoUrl: images.validIdSelfieUrl,
      licenseSelfieUrl: images.licenseSelfieUrl,
      driverLicenseSelfieUrl: images.licenseSelfieUrl,
      idExpiryDate: verification.idExpiryDate || null,
      licenseExpiryDate: verification.licenseExpiryDate || null,
      verificationExpiresAt: verification.verificationExpiresAt || null,
      verificationExpiryStatus: verification.verificationExpiryStatus || "unknown",
      expiryDaysRemaining: verification.expiryDaysRemaining ?? null,
      expiryReminderDaysBefore:
        verification.expiryReminderDaysBefore || VERIFICATION_EXPIRY_WARNING_DAYS,
      idHasNoExpiry: Boolean(verification.idHasNoExpiry),
      licenseHasNoExpiry: Boolean(verification.licenseHasNoExpiry),
      approvedAt: verification.approvedAt || null,
      approvedBy: verification.approvedBy || null,
      reviewedBy: verification.reviewedBy || null,
      status: overallStatus,
      overallVerificationStatus: overallStatus,
      overallStatus,
      verificationLevel,
      validIdStatus,
      driverLicenseStatus,
      licenseStatus: driverLicenseStatus,
      adminRemarks: verification.adminRemarks || verificationRemarks,
    };
  };

  const displayedUsers = useMemo(() => {
    if (verificationFilter === "expiring") {
      return users.filter((item) => {
        const verification = item?.verification;
        if (!verification) return false;
        return getExpiryInfo(normalizeVerificationForUi(verification)).status === "expiring_soon";
      });
    }
    if (verificationFilter === "pending_review") {
      return users.filter((item) => needsVerificationReview(item));
    }
    return users;
    // normalizeVerificationForUi and needsVerificationReview are local helpers; deps on users + filter is enough.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users, verificationFilter]);

  const pendingReviewCount = useMemo(
    () => users.filter((item) => needsVerificationReview(item)).length,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [users]
  );

  const openVerificationModal = async (u) => {
    setSelectedVerificationUser(u);
    setVerificationRemarks(u?.verification?.adminRemarks || "");
    setVerificationError("");
    setVerificationExpiryForm({
      validId: {
        expiryDate: formatDateInputValue(
          u?.verification?.idExpiryDate || u?.verification?.verificationExpiresAt
        ),
        noExpiry: Boolean(u?.verification?.idHasNoExpiry),
      },
      driverLicense: {
        expiryDate: formatDateInputValue(
          u?.verification?.licenseExpiryDate || u?.verification?.verificationExpiresAt
        ),
        noExpiry: Boolean(u?.verification?.licenseHasNoExpiry),
      },
    });
    setMenuOpenId(null);

    try {
      const res = await api.get("/admin/verifications");
      const freshVerification = (res.data.verifications || []).find((item) => {
        const userId = item.userId?._id || item.userId;
        return String(userId || "") === String(u?._id || "");
      });

      if (!freshVerification) return;

      const normalizedVerification = normalizeVerificationForUi(freshVerification);
      setSelectedVerificationUser((prev) =>
        prev && String(prev._id || "") === String(u?._id || "")
          ? {
              ...prev,
              verification: normalizedVerification,
              isVerified: normalizedVerification.verificationLevel !== "none",
            }
          : prev
      );
      setUsers((prev) =>
        prev.map((item) =>
          String(item._id || "") === String(u?._id || "")
            ? {
                ...item,
                verification: normalizedVerification,
                isVerified: normalizedVerification.verificationLevel !== "none",
              }
            : item
        )
      );
      setVerificationRemarks(normalizedVerification.adminRemarks || "");
      setVerificationExpiryForm({
        validId: {
          expiryDate: formatDateInputValue(
            normalizedVerification.idExpiryDate ||
              normalizedVerification.verificationExpiresAt
          ),
          noExpiry: Boolean(normalizedVerification.idHasNoExpiry),
        },
        driverLicense: {
          expiryDate: formatDateInputValue(
            normalizedVerification.licenseExpiryDate ||
              normalizedVerification.verificationExpiresAt
          ),
          noExpiry: Boolean(normalizedVerification.licenseHasNoExpiry),
        },
      });
    } catch {
      // Keep the modal usable with the list data if the fresh verification fetch fails.
    }
  };

  const closeVerificationModal = () => {
    if (verificationActionLoading) return;
    setSelectedVerificationUser(null);
    setVerificationRemarks("");
    setVerificationError("");
    setVerificationExpiryForm({});
    setDocumentPreview(null);
    setVerificationConfirm(null);
  };

  const openDocumentPreview = ({ title, activeSide, frontImage, backImage }) => {
    const activeImage = activeSide === "back" ? backImage : frontImage;
    if (!activeImage) return;

    setDocumentPreview({
      title,
      activeSide,
      images: {
        front: frontImage,
        back: backImage,
      },
    });
  };

  const flipDocumentPreview = () => {
    setDocumentPreview((current) => {
      if (!current) return current;

      const nextSide = current.activeSide === "front" ? "back" : "front";
      if (!current.images?.[nextSide]) return current;

      return {
        ...current,
        activeSide: nextSide,
      };
    });
  };

  const performDocumentVerificationUpdate = async (documentKey, statusToSet) => {
    const verificationId = selectedVerificationUser?.verification?._id;
    if (!verificationId) return;
    const canUpdate =
      documentKey === "validId"
        ? hasValidIdDocument(selectedVerificationUser.verification)
        : hasLicenseDocument(selectedVerificationUser.verification);
    if (!canUpdate) return;

    const payload =
      documentKey === "validId"
        ? { validIdStatus: statusToSet }
        : { driverLicenseStatus: statusToSet };
    const expiryDraft = verificationExpiryForm[documentKey] || {};
    const isApproval = statusToSet === "approved";

    if (statusToSet === "rejected" && !verificationRemarks.trim()) {
      setVerificationError("Rejection remarks are required.");
      return;
    }

    if (statusToSet === "needs_update" && !verificationRemarks.trim()) {
      setVerificationError("Remarks are required before requesting updated documents.");
      return;
    }

    if (isApproval) {
      if (!expiryDraft.noExpiry && !expiryDraft.expiryDate) {
        setVerificationError(
          documentKey === "validId"
            ? "ID expiry date is required before approval."
            : "License expiry date is required before approval."
        );
        return;
      }

      if (documentKey === "validId") {
        payload.idHasNoExpiry = Boolean(expiryDraft.noExpiry);
        payload.idExpiryDate = expiryDraft.noExpiry ? "" : expiryDraft.expiryDate;
      } else {
        payload.licenseHasNoExpiry = Boolean(expiryDraft.noExpiry);
        payload.licenseExpiryDate = expiryDraft.noExpiry ? "" : expiryDraft.expiryDate;
      }
    }

    setMsg("");
    setVerificationError("");
    setVerificationConfirm(null);
    setVerificationActionLoading(`${documentKey}:${statusToSet}`);

    try {
      let res;
      try {
        res = await api.patch(`/admin/verifications/${verificationId}`, {
          ...payload,
          adminRemarks: verificationRemarks,
        });
      } catch (err) {
        if (err?.response?.status !== 404) throw err;
        res = await api.patch(`/users/${selectedVerificationUser._id}/verification`, {
          ...payload,
          adminRemarks: verificationRemarks,
        });
      }

      const updatedVerification = normalizeVerificationForUi(
        res?.data?.verification ||
          res?.data?.user?.verification ||
          {
            ...selectedVerificationUser.verification,
            ...payload,
          }
      );

      if (updatedVerification) {
        setSelectedVerificationUser((prev) =>
          prev
            ? {
                ...prev,
                isVerified: updatedVerification.verificationLevel !== "none",
                verification: updatedVerification,
              }
            : prev
        );

        setUsers((prev) =>
          prev.map((item) =>
            item._id === selectedVerificationUser._id
              ? {
                  ...item,
                  isVerified: updatedVerification.verificationLevel !== "none",
                  verification: updatedVerification,
                }
              : item
          )
        );
      }

      await loadUsers();
      setMsg(
        `${documentKey === "validId" ? "Valid ID" : "Driver's License"} ${statusToSet}.`
      );
      setVerificationConfirm(null);
    } catch (err) {
      setMsg(err?.response?.data?.message || "Failed to update document status.");
    } finally {
      setVerificationActionLoading("");
    }
  };

  const updateDocumentVerification = (documentKey, statusToSet) => {
    if (verificationActionLoading) return;
    const label = documentKey === "validId" ? "Valid ID" : "Driver's License";
    const isApproval = statusToSet === "approved";
    const isRejection = statusToSet === "rejected";
    const expiryDraft = verificationExpiryForm[documentKey] || {};

    if (isRejection && !verificationRemarks.trim()) {
      setVerificationError("Rejection remarks are required.");
      return;
    }

    if (isApproval && !expiryDraft.noExpiry && !expiryDraft.expiryDate) {
      setVerificationError(
        documentKey === "validId"
          ? "ID expiry date is required before approval."
          : "License expiry date is required before approval."
      );
      return;
    }

    setVerificationError("");
    setVerificationConfirm({
      documentKey,
      statusToSet,
      title: `${isApproval ? "Approve" : "Reject"} ${label}?`,
      message: isApproval
        ? `This will mark the ${label} as approved and update the customer's verification status.`
        : `This will reject the ${label} and send your remarks to the customer.`,
      confirmLabel: isApproval ? "Approve" : "Reject",
      loadingLabel: isApproval ? "Approving..." : "Rejecting...",
      tone: isRejection ? "danger" : "success",
    });
  };

  const openRequestUpdateModal = (documentKey, expiryInfo) => {
    const isExpired = expiryInfo?.status === "expired";
    const isExpiringSoon = expiryInfo?.status === "expiring_soon";
    setRequestUpdateReason(
      isExpired ? "expired_document" : isExpiringSoon ? "expiring_soon" : ""
    );
    setRequestUpdateRemarks("");
    setRequestUpdateError("");
    setRequestUpdateModal({ documentKey });
  };

  const submitRequestUpdate = async () => {
    if (!requestUpdateModal?.documentKey || requestUpdateSubmitting) return;
    if (!requestUpdateReason) {
      setRequestUpdateError("Please select a reason.");
      return;
    }
    if (!requestUpdateRemarks.trim()) {
      setRequestUpdateError("Please add remarks for the customer.");
      return;
    }
    const reasonEntry = REQUEST_UPDATE_REASONS.find((r) => r.value === requestUpdateReason);
    const reasonLabel = reasonEntry?.label || requestUpdateReason;
    const isSoftReason = Boolean(reasonEntry?.soft);
    // Soft reasons (expiring soon) use renewal_reminder to keep document valid; hard reasons use needs_update
    const documentStatus = isSoftReason ? "renewal_reminder" : "needs_update";
    const combinedRemarks = `[${reasonLabel}] ${requestUpdateRemarks.trim()}`;
    setVerificationRemarks(combinedRemarks);
    setRequestUpdateError("");
    setRequestUpdateSubmitting(true);
    try {
      const verificationId = selectedVerificationUser?.verification?._id;
      if (!verificationId) return;
      const payload =
        requestUpdateModal.documentKey === "validId"
          ? { validIdStatus: documentStatus }
          : { driverLicenseStatus: documentStatus };
      let res;
      try {
        res = await api.patch(`/admin/verifications/${verificationId}`, {
          ...payload,
          adminRemarks: combinedRemarks,
        });
      } catch (err) {
        if (err?.response?.status !== 404) throw err;
        res = await api.patch(`/users/${selectedVerificationUser._id}/verification`, {
          ...payload,
          adminRemarks: combinedRemarks,
        });
      }
      const updatedVerification = normalizeVerificationForUi(
        res?.data?.verification || res?.data?.user?.verification || {
          ...selectedVerificationUser.verification,
          ...payload,
          adminRemarks: combinedRemarks,
        }
      );
      if (updatedVerification) {
        setSelectedVerificationUser((prev) =>
          prev ? { ...prev, verification: updatedVerification } : prev
        );
        setUsers((prev) =>
          prev.map((item) =>
            item._id === selectedVerificationUser._id
              ? { ...item, verification: updatedVerification }
              : item
          )
        );
      }
      await loadUsers();
      setMsg(`${requestUpdateModal.documentKey === "validId" ? "Valid ID" : "Driver's License"} update requested.`);
      setRequestUpdateModal(null);
    } catch (err) {
      setRequestUpdateError(err?.response?.data?.message || "Failed to request update. Please try again.");
    } finally {
      setRequestUpdateSubmitting(false);
    }
  };

  const renderDocumentSide = (label, image, previewConfig) => (
    <div className="verification-review-card">
      <div className="verification-review-card__header">
        <label className="field-label" style={{ fontSize: 12 }}>{label}</label>
        {image ? (
          <button
            type="button"
            className="verification-review-card__link"
            onClick={() => openDocumentPreview(previewConfig)}
          >
            View
          </button>
        ) : null}
      </div>
      {image ? (
        <button
          type="button"
          className="verification-thumb-button"
          onClick={() => openDocumentPreview(previewConfig)}
          title="Preview document"
        >
          <img src={image} alt={label} />
        </button>
      ) : (
        <div className="verification-review-card__missing">
          {label.includes("Face Verification Photo")
            ? "Face verification photo not available for this older record."
            : "Document not available."}
        </div>
      )}
    </div>
  );

  const renderDocumentSection = ({
    title,
    documentKey,
    status,
    frontImage,
    backImage,
    faceImage,
    approveLabel,
    rejectLabel,
    expiryInfo,
  }) => {
    const hasDocImages = Boolean(frontImage && backImage);
    const hasFace = Boolean(faceImage);
    const hasCompleteDocument = hasDocImages && hasFace;
    const normalizedStatus = String(status || "").toLowerCase();
    const canReviewDocument =
      hasCompleteDocument &&
      (isReviewStatus(status) || normalizedStatus === "rejected" || normalizedStatus === "needs_update");
    const isApprovedDocument = normalizedStatus === "approved";
    const isExpired = expiryInfo?.status === "expired";
    const isExpiringSoon = expiryInfo?.status === "expiring_soon";
    const requestUpdateLabel =
      isExpired || isExpiringSoon ? "Request Renewal" : "Request Update";
    const expiryDraft = verificationExpiryForm[documentKey] || {
      expiryDate: "",
      noExpiry: false,
    };

    return (
      <section className="verification-review-section">
        <div className="verification-review-section__head">
          <div>
            <label className="field-label">{title}</label>
            <p className="muted">Review the submitted document images before approving or rejecting.</p>
          </div>
          <span className={getDocumentBadge(status)}>
            {formatDocumentStatus(status)}
          </span>
        </div>

        <div className="verification-doc-grid">
          {renderDocumentSide(
            title === "Driver's License"
              ? "Driver's License Front"
              : "Government ID Front",
            frontImage,
            { title, activeSide: "front", frontImage, backImage }
          )}
          {renderDocumentSide(
            title === "Driver's License"
              ? "Driver's License Back"
              : "Government ID Back",
            backImage,
            { title, activeSide: "back", frontImage, backImage }
          )}
          {renderDocumentSide(`${title} Face Verification Photo`, faceImage, {
            title: `${title} Face Verification Photo`,
            activeSide: "front",
            frontImage: faceImage,
            backImage: "",
          })}
        </div>

        {isApprovedDocument ? (
          <div className="verification-expiry-display">
            <span className="field-label">Document Expiry</span>
            <strong>
              {expiryInfo?.status === "no_expiry"
                ? "No expiry shown"
                : expiryInfo?.date
                ? `${expiryInfo.date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}${isExpired ? " — Expired" : isExpiringSoon ? ` — Expiring in ${expiryInfo.daysRemaining} day(s)` : ""}`
                : "Expiry date not set"}
            </strong>
          </div>
        ) : null}

        {canReviewDocument ? (
          <div className="verification-expiry-editor">
            <div>
              <label className="field-label">
                {documentKey === "validId" ? "ID Expiry Date" : "License Expiry Date"}
                <span className="field-required"> *</span>
              </label>
              <input
                type="date"
                className="input"
                min={new Date().toISOString().split("T")[0]}
                value={expiryDraft.expiryDate || ""}
                disabled={Boolean(expiryDraft.noExpiry)}
                onChange={(event) =>
                  setVerificationExpiryForm((prev) => ({
                    ...prev,
                    [documentKey]: {
                      ...(prev[documentKey] || {}),
                      expiryDate: event.target.value,
                    },
                  }))
                }
              />
              <span className="muted">
                Required — enter the expiry date shown on the submitted document.
              </span>
            </div>
            <label className="verification-no-expiry">
              <input
                type="checkbox"
                checked={Boolean(expiryDraft.noExpiry)}
                onChange={(event) =>
                  setVerificationExpiryForm((prev) => ({
                    ...prev,
                    [documentKey]: {
                      ...(prev[documentKey] || {}),
                      noExpiry: event.target.checked,
                    },
                  }))
                }
              />
              No expiry shown on this document
            </label>
          </div>
        ) : null}

        {canReviewDocument ? (
          <div className="btn-row" style={{ marginTop: 10 }}>
            <button
              type="button"
              className="btn btn-success"
              disabled={Boolean(verificationActionLoading)}
              onClick={() => updateDocumentVerification(documentKey, "approved")}
            >
              {verificationActionLoading === `${documentKey}:approved` ? "Approving..." : approveLabel}
            </button>
            <button
              type="button"
              className="btn btn-danger"
              disabled={Boolean(verificationActionLoading)}
              onClick={() => updateDocumentVerification(documentKey, "rejected")}
            >
              {verificationActionLoading === `${documentKey}:rejected` ? "Rejecting..." : rejectLabel}
            </button>
          </div>
        ) : null}

        {isApprovedDocument ? (
          <div className="btn-row" style={{ marginTop: 10 }}>
            <button
              type="button"
              className={`btn ${isExpired || isExpiringSoon ? "btn-warning" : "btn-soft"}`}
              onClick={() => openRequestUpdateModal(documentKey, expiryInfo)}
              disabled={Boolean(verificationActionLoading)}
            >
              {requestUpdateLabel}
            </button>
          </div>
        ) : null}
      </section>
    );
  };

  if (!canUseUsersModule) {
    return <Navigate to="/unauthorized" replace />;
  }

  const usersLoadingTitle = verificationActionLoading.includes(":approved")
    ? "Approving verification..."
    : verificationActionLoading.includes(":rejected")
    ? "Rejecting verification..."
    : requestUpdateSubmitting
    ? "Requesting update..."
    : statusActionLoading
    ? "Reactivating account..."
    : deactivateModal?.submitting
    ? "Deactivating account..."
    : "";

  return (
    <AdminShell
      pageClassName="admin-shell--rental-management"
      eyebrow="ADMINISTRATION"
      title="Rental Management"
      subtitle="Manage admin, staff, and technical admin access for rental operations."
      actions={
        isTechnicalAdmin ? (
        <button type="button" className="btn btn-primary" onClick={openCreateModal}>
          Create Account
        </button>
        ) : null
      }
    >
      <AdminLoadingOverlay
        open={Boolean(usersLoadingTitle)}
        title={usersLoadingTitle}
        message="Please wait while FleetX saves the admin action."
      />
      {msg && <div className="admin-card muted">{msg}</div>}

      <div className="admin-card rental-management-filters">
        <div className="section-head">
          <div>
            <h3 className="admin-section-title">Filters</h3>
            <span className="muted">
              Search by name, email, or filter by account status.
            </span>
          </div>

          <button type="button" className="btn btn-soft" onClick={loadUsers}>
            Refresh
          </button>
        </div>

        <div className="user-toolbar">
          <input
            className="input"
            placeholder="Search accounts by name or email"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <select
            className="select"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <option value="all">All accounts</option>
            <option value="active">Active accounts</option>
            <option value="inactive">Inactive accounts</option>
          </select>

          <select
            className="select"
            value={verificationFilter}
            onChange={(e) => setVerificationFilter(e.target.value)}
          >
            {VERIFICATION_FILTER_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="admin-card rental-management-accounts">
        <div className="section-head" style={{ marginBottom: 12 }}>
          <h3 className="admin-section-title" style={{ margin: 0 }}>
            Accounts List
            {verificationFilter === "pending_review" && displayedUsers.length > 0 ? (
              <span className="badge badge-pending" style={{ marginLeft: 8, fontSize: 11 }}>
                {displayedUsers.length} pending
              </span>
            ) : null}
          </h3>
          {pendingReviewCount > 0 && verificationFilter !== "pending_review" ? (
            <button
              type="button"
              className="btn btn-soft"
              style={{ fontSize: 12 }}
              onClick={() => setVerificationFilter("pending_review")}
            >
              {pendingReviewCount} pending review
            </button>
          ) : null}
        </div>

        <div className="table-wrap">
          <table className="table user-table">
            <thead>
              <tr>
                <th>Account</th>
                <th>Role</th>
                <th>Verification</th>
                <th>Status</th>
                <th style={{ minWidth: 180 }}>Actions</th>
              </tr>
            </thead>

            <tbody>
              {displayedUsers.map((u, index) => {
                const adminPortalAccount = isAdminPortalAccount(u);
                const ownAccount = [user?.id, user?._id]
                  .filter(Boolean)
                  .some((id) => String(id) === String(u._id));
                const canManageAccount =
                  isTechnicalAdmin || (isAdmin && !adminPortalAccount);
                const canEditAccount = ownAccount || canManageAccount;
                const protectsTechnicalAdmin =
                  isTechnicalAdminAccount(u) && !isTechnicalAdmin;
                const protectsOwnAccount = ownAccount && adminPortalAccount;

                return (
                <tr
                  key={u._id}
                  className={index % 2 === 0 ? "row-even" : "row-odd"}
                >
                  <td>
                    <div className="cell-title">{getUserDisplayName(u)}</div>
                    <div className="cell-sub">{u.email || "No Email"}</div>
                  </td>

                  <td>
                    <span className="user-role-pill" data-role={u.role}>{getRoleLabel(u.role)}</span>
                  </td>

                  <td>
                    <span className={getVerificationBadge(u)} data-verification-status={getVerificationStatus(u)}>
                      {getVerificationStatus(u)}
                    </span>
                  </td>

                  <td>
                    <span className={getStatusBadge(u.isActive)}>
                      {u.isActive ? "Active" : "Inactive"}
                    </span>
                  </td>

                  <td>
                    <div className="action-inline">
                      {canEditAccount ? (
                        <button
                          type="button"
                          className="vehicle-icon-btn user-action-icon-btn"
                          onClick={() => openEditModal(u)}
                          disabled={protectsTechnicalAdmin}
                          title="Edit account"
                          aria-label={`Edit ${getUserDisplayName(u, "account")}`}
                        >
                          <EditIcon />
                        </button>
                      ) : null}

                      {hasViewableVerificationDocuments(u) ? (
                        <button
                          type="button"
                          className="btn user-review-btn"
                          onClick={() => openVerificationModal(u)}
                        >
                          {needsVerificationReview(u)
                            ? "Review Verification"
                            : "View Documents"}
                        </button>
                      ) : null}

                      {canManageAccount ? (
                        <div
                          className="action-menu-wrap"
                          onClick={(e) => e.stopPropagation()}
                        >
                        <button
                          type="button"
                          className="vehicle-icon-btn user-action-icon-btn"
                          onClick={() =>
                            setMenuOpenId(menuOpenId === u._id ? null : u._id)
                          }
                          title="More actions"
                          aria-label={`More actions for ${getUserDisplayName(u, "account")}`}
                        >
                          <MoreIcon />
                        </button>

                        {menuOpenId === u._id && (
                          <div className="action-menu-dropdown user-action-dropdown">
                            {u.isActive ? (
                              <button
                                type="button"
                                className="action-menu-item warning"
                                disabled={protectsTechnicalAdmin || protectsOwnAccount || Boolean(statusActionLoading) || Boolean(deactivateModal?.submitting)}
                                onClick={() => deactivate(u)}
                              >
                                Deactivate Account
                              </button>
                            ) : (
                              <button
                                type="button"
                                className="action-menu-item success"
                                disabled={Boolean(statusActionLoading) || Boolean(deactivateModal?.submitting)}
                                onClick={() => {
                                  setMenuOpenId(null);
                                  activate(u._id);
                                }}
                              >
                                Reactivate Account
                              </button>
                            )}
                          </div>
                        )}
                        </div>
                      ) : null}
                    </div>
                  </td>
                </tr>
                );
              })}

              {displayedUsers.length === 0 && (
                <tr>
                  <td colSpan="5" className="muted">
                    {verificationFilter === "pending_review"
                      ? "No verification requests need review right now."
                      : "No accounts match the current filters."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showCreateModal && (
        <div className="modal-overlay vehicle-modal-overlay account-create-modal-overlay" onClick={closeCreateModal}>
          <div className="modal-box account-create-modal" onClick={(e) => e.stopPropagation()}>
            <header className="account-create-modal__header">
              <div className="account-create-modal__heading">
                <span className="account-create-modal__icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24">
                    <circle cx="12" cy="8" r="3.25" />
                    <path d="M5.5 19c.45-3.65 2.6-5.5 6.5-5.5s6.05 1.85 6.5 5.5" />
                    <path d="M18.5 5.5v4M16.5 7.5h4" />
                  </svg>
                </span>
                <div>
                  <span className="account-create-modal__eyebrow">ACCOUNT SETUP</span>
                  <h3>Create Starter Account</h3>
                  <p>
                    Create a staff or administrator account. The account holder can complete their profile after signing in.
                  </p>
                </div>
              </div>

              <button type="button" className="account-create-modal__close" onClick={closeCreateModal} aria-label="Close account form">
                X
              </button>
            </header>

            <form onSubmit={createUser} className="account-create-modal__form">
              <div className="account-create-modal__body">
                <section className="account-create-modal__section">
                  <div className="account-create-modal__section-heading">
                    <span>1</span>
                    <div>
                      <h4>Account Information</h4>
                      <p>Enter the account holder's details and access level.</p>
                    </div>
                  </div>
                  <div className="form-grid account-create-modal__grid">
                <div className="field-group">
                  <label className="field-label">First Name</label>
                  <input
                    className={`input ${createErrors.firstName ? "input-error" : ""}`}
                    placeholder="First name"
                    value={createForm.firstName}
                    onChange={(e) =>
                      setCreateForm({ ...createForm, firstName: e.target.value })
                    }
                  />
                  {createErrors.firstName && (
                    <span className="field-error">{createErrors.firstName}</span>
                  )}
                </div>

                <div className="field-group">
                  <label className="field-label">Last Name</label>
                  <input
                    className={`input ${createErrors.lastName ? "input-error" : ""}`}
                    placeholder="Last name"
                    value={createForm.lastName}
                    onChange={(e) =>
                      setCreateForm({ ...createForm, lastName: e.target.value })
                    }
                  />
                  {createErrors.lastName && (
                    <span className="field-error">{createErrors.lastName}</span>
                  )}
                </div>

                <div className="field-group">
                  <label className="field-label">Email</label>
                  <input
                    className={`input ${createErrors.email ? "input-error" : ""}`}
                    placeholder="Email"
                    value={createForm.email}
                    onChange={(e) => {
                      setCreateForm({ ...createForm, email: e.target.value });
                      setCreateErrors((prev) => ({ ...prev, email: "" }));
                    }}
                  />
                  {createErrors.email && (
                    <span className="field-error">{createErrors.email}</span>
                  )}
                </div>

                <div className="field-group">
                  <label className="field-label">Contact Number</label>
                  <div className={`admin-phone-row ${createErrors.contactNumber ? "is-invalid" : ""}`}>
                    <select
                      className="select admin-phone-row__code"
                      value={createForm.contactCountryCode}
                      onChange={(e) => updateCreateContactCountry(e.target.value)}
                      aria-label="Country code"
                    >
                      {COUNTRY_CODE_OPTIONS.map((option) => (
                        <option
                          key={`${option.country}-${option.code}`}
                          value={option.code}
                        >
                          {getCountryOptionLabel(option)}
                        </option>
                      ))}
                    </select>
                    <input
                      className="input admin-phone-row__number"
                      type="tel"
                      inputMode="tel"
                      placeholder="Enter contact number"
                      value={createForm.contactNumber}
                      onChange={(e) => updateCreateContactNumber(e.target.value)}
                      maxLength={getMaxLocalPhoneDigits(
                        createForm.contactCountryCode,
                        createForm.contactNumber
                      )}
                    />
                  </div>
                  {createErrors.contactNumber && (
                    <span className="field-error">{createErrors.contactNumber}</span>
                  )}
                </div>

                <div className="field-group">
                  <label className="field-label">Role</label>
                  <select
                    className="select"
                    value={createForm.role}
                    onChange={(e) =>
                      setCreateForm({ ...createForm, role: e.target.value })
                    }
                  >
                    {ADMIN_ROLE_OPTIONS.map((option) => (
                      <option
                        key={option.value}
                        value={option.value}
                        disabled={option.value === "technical_admin" && !isTechnicalAdmin}
                      >
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="field-group">
                  <label className="field-label">Account Status</label>
                  <select
                    className="select"
                    value={createForm.isActive ? "active" : "inactive"}
                    onChange={(e) =>
                      setCreateForm({
                        ...createForm,
                        isActive: e.target.value === "active",
                      })
                    }
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
                  </div>
                </section>
              </div>

              <footer className="account-create-modal__footer">
                <button type="button" className="btn" onClick={closeCreateModal}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Create Account
                </button>
              </footer>
            </form>
          </div>
        </div>
      )}

      {selectedVerificationUser && (
        <div className="modal-overlay" onClick={closeVerificationModal}>
          <div className="modal-box" onClick={(e) => e.stopPropagation()}>
            {(() => {
              const verification = selectedVerificationUser.verification || {};
              const images = getVerificationImages(verification);
              const validIdStatus = getDocumentStatusForUi(verification, "validId");
              const driverLicenseStatus = getDocumentStatusForUi(
                verification,
                "driverLicense"
              );
              const customerContact =
                selectedVerificationUser.contact ||
                selectedVerificationUser.contactNumber ||
                "Not available";
              const verificationTypeLabel = getVerificationTypeLabel(
                selectedVerificationUser,
                verification
              );
              const expiryInfo = getExpiryInfo(verification);
              const validIdExpiryInfo = getExpiryInfo({ ...verification, verificationExpiresAt: verification.idExpiryDate });
              const licenseExpiryInfo = getExpiryInfo({ ...verification, verificationExpiresAt: verification.licenseExpiryDate });
              const hasValidIdImages = Boolean(images.validIdFront || images.validIdBack);
              const hasLicenseImages = Boolean(images.licenseFront || images.licenseBack);
              const showLicense =
                hasLicenseImages ||
                ["driver_license", "drivers_license", "self_drive"].includes(
                  String(verification.verificationType || selectedVerificationUser.verificationType || "").toLowerCase()
                );
              const showValidId =
                hasValidIdImages ||
                (!showLicense && !hasLicenseImages) ||
                String(validIdStatus || "").toLowerCase() !== "not_required";
              const documentSections = [
                showValidId
                  ? {
                      title: "Government ID",
                      documentKey: "validId",
                      status: validIdStatus,
                      frontImage: images.validIdFront,
                      backImage: images.validIdBack,
                      faceImage: images.validIdSelfieUrl,
                      approveLabel: "Approve Government ID",
                      rejectLabel: "Reject Government ID",
                      expiryInfo: validIdExpiryInfo,
                    }
                  : null,
                showLicense
                  ? {
                      title: "Driver's License",
                      documentKey: "driverLicense",
                      status: driverLicenseStatus,
                      frontImage: images.licenseFront,
                      backImage: images.licenseBack,
                      faceImage: images.licenseSelfieUrl,
                      approveLabel: "Approve Driver's License",
                      rejectLabel: "Reject Driver's License",
                      expiryInfo: licenseExpiryInfo,
                    }
                  : null,
              ].filter(Boolean);
              return (
                <>
                  <div className="modal-header verification-review-header">
                    <div>
                      <span className="muted">Verification Review</span>
                      <h3 className="admin-section-title" style={{ margin: "4px 0 0" }}>
                        {getUserDisplayName(selectedVerificationUser)}
                      </h3>
                    </div>
                    <div className="verification-review-header__meta">
                      <span className={getVerificationBadge(selectedVerificationUser)}>
                        {getVerificationStatus(selectedVerificationUser)}
                      </span>
                      <span className="badge">{verificationTypeLabel}</span>
                      <button
                        type="button"
                        className="btn-close"
                        onClick={closeVerificationModal}
                      >
                        Close
                      </button>
                    </div>
                  </div>

                  <div className="verification-review-layout">
                    <section className="verification-review-panel">
                      <div className="verification-review-panel__head">
                        <h4>Customer Info</h4>
                      </div>
                      <div className="verification-info-grid">
                        <div>
                          <span>Name</span>
                          <strong>{getUserDisplayName(selectedVerificationUser)}</strong>
                        </div>
                        <div>
                          <span>Email</span>
                          <strong>{selectedVerificationUser.email || "No Email"}</strong>
                        </div>
                        <div>
                          <span>Contact</span>
                          <strong>{customerContact}</strong>
                        </div>
                        <div>
                          <span>Submitted</span>
                          <strong>
                            {formatVerificationDate(
                              verification.submittedAt ||
                                verification.verificationSubmittedAt ||
                                selectedVerificationUser.verificationSubmittedAt
                            )}
                          </strong>
                        </div>
                        <div>
                          <span>
                            {["basic_verified", "fully_verified"].includes(
                              String(verification.overallVerificationStatus || verification.overallStatus || "").toLowerCase()
                            )
                              ? "Approved"
                              : "Reviewed"}
                          </span>
                          <strong>
                            {formatVerificationDate(verification.approvedAt || verification.reviewedAt)}
                          </strong>
                        </div>
                        <div>
                          <span>Reviewed By</span>
                          <strong>
                            {formatReviewerName(verification.approvedBy || verification.reviewedBy)}
                          </strong>
                        </div>
                      </div>
                    </section>

                    <section className="verification-review-panel">
                      <div className="verification-review-panel__head">
                        <h4>Expiry Details</h4>
                        <span className={`badge badge-${expiryInfo.tone}`}>
                          {expiryInfo.label}
                        </span>
                      </div>
                      <div className="verification-info-grid">
                        <div>
                          <span>Expiry Date</span>
                          <strong>
                            {expiryInfo.date
                              ? formatVerificationDate(expiryInfo.date)
                              : expiryInfo.status === "no_expiry"
                              ? "No expiry shown"
                              : "Not set"}
                          </strong>
                        </div>
                        <div>
                          <span>Days Remaining</span>
                          <strong>
                            {expiryInfo.daysRemaining === null
                              ? "Not available"
                              : expiryInfo.daysRemaining >= 0
                              ? `${expiryInfo.daysRemaining} day(s)`
                              : `${Math.abs(expiryInfo.daysRemaining)} day(s) expired`}
                          </strong>
                        </div>
                      </div>
                    </section>

                    <section className="verification-review-panel verification-review-panel--documents">
                      <div className="verification-review-panel__head">
                        <h4>Documents Review</h4>
                      </div>
                      {documentSections.map((section) => (
                        <div key={section.documentKey}>
                          {renderDocumentSection(section)}
                        </div>
                      ))}
                    </section>

                    <section className="verification-review-panel">
                      <div className="verification-review-panel__head">
                        <h4>Remarks</h4>
                        <span className="muted" style={{ fontSize: 12 }}>Required for rejection or update request</span>
                      </div>
                      <textarea
                        className="input"
                        rows="4"
                        value={verificationRemarks}
                        onChange={(e) => {
                          setVerificationRemarks(e.target.value);
                          setVerificationError("");
                        }}
                        placeholder="Add a short note for the customer."
                      />
                      {verificationError ? (
                        <span className="field-error">{verificationError}</span>
                      ) : null}
                      {verification.adminRemarks ? (
                        <div className="verification-remarks-note">
                          <strong>Previous remarks:</strong> {verification.adminRemarks}
                        </div>
                      ) : null}
                    </section>
                  </div>

                </>
              );
            })()}
          </div>
        </div>
      )}

      {requestUpdateModal ? (
        <div className="modal-overlay" onClick={() => setRequestUpdateModal(null)}>
          <div className="modal-box modal-box--sm" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h3 className="admin-section-title" style={{ margin: 0 }}>
                  {requestUpdateModal.documentKey === "validId" ? "Request ID Update" : "Request License Update"}
                </h3>
                <span className="muted">Customer upload fields will be unlocked after submission.</span>
              </div>
              <button type="button" className="btn-close" onClick={() => setRequestUpdateModal(null)}>
                Close
              </button>
            </div>
            <div className="field-group">
              <label className="field-label">Reason <span className="field-required">*</span></label>
              <select
                className="select"
                value={requestUpdateReason}
                onChange={(e) => { setRequestUpdateReason(e.target.value); setRequestUpdateError(""); }}
              >
                <option value="">Select a reason…</option>
                {REQUEST_UPDATE_REASONS.map((r) => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </select>
              <label className="field-label" style={{ marginTop: 12 }}>
                Remarks for customer <span className="field-required">*</span>
              </label>
              <textarea
                className="input"
                rows="3"
                value={requestUpdateRemarks}
                onChange={(e) => { setRequestUpdateRemarks(e.target.value); setRequestUpdateError(""); }}
                placeholder="Describe what needs to be updated or resubmitted."
              />
              {requestUpdateError ? <span className="field-error">{requestUpdateError}</span> : null}
            </div>
            <div className="btn-row" style={{ marginTop: 12 }}>
              <button type="button" className="btn" onClick={() => setRequestUpdateModal(null)}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-warning"
                onClick={submitRequestUpdate}
                disabled={requestUpdateSubmitting}
              >
                {requestUpdateSubmitting ? "Submitting..." : "Submit Request"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {verificationConfirm ? (
        <div className="modal-overlay" onClick={() => !verificationActionLoading && setVerificationConfirm(null)}>
          <div
            className="modal-box modal-box--sm"
            role="dialog"
            aria-modal="true"
            aria-labelledby="verification-action-confirm-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3 id="verification-action-confirm-title" className="admin-section-title" style={{ margin: 0 }}>
                {verificationConfirm.title}
              </h3>
              <button
                type="button"
                className="btn-close"
                onClick={() => setVerificationConfirm(null)}
                disabled={Boolean(verificationActionLoading)}
              >
                Close
              </button>
            </div>
            <p className="muted">{verificationConfirm.message}</p>
            <div className="btn-row" style={{ marginTop: 16 }}>
              <button
                type="button"
                className="btn"
                onClick={() => setVerificationConfirm(null)}
                disabled={Boolean(verificationActionLoading)}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`btn ${verificationConfirm.tone === "danger" ? "btn-danger" : "btn-success"}`}
                disabled={Boolean(verificationActionLoading)}
                onClick={() =>
                  performDocumentVerificationUpdate(
                    verificationConfirm.documentKey,
                    verificationConfirm.statusToSet
                  )
                }
              >
                {verificationActionLoading
                  ? verificationConfirm.loadingLabel
                  : verificationConfirm.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {statusConfirm ? (
        <div className="modal-overlay" onClick={() => !statusActionLoading && setStatusConfirm(null)}>
          <div
            className="modal-box modal-box--sm"
            role="dialog"
            aria-modal="true"
            aria-labelledby="account-status-confirm-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3 id="account-status-confirm-title" className="admin-section-title" style={{ margin: 0 }}>
                {statusConfirm.title}
              </h3>
              <button
                type="button"
                className="btn-close"
                onClick={() => setStatusConfirm(null)}
                disabled={Boolean(statusActionLoading)}
              >
                Close
              </button>
            </div>
            <p className="muted">{statusConfirm.message}</p>
            <div className="btn-row" style={{ marginTop: 16 }}>
              <button
                type="button"
                className="btn"
                onClick={() => setStatusConfirm(null)}
                disabled={Boolean(statusActionLoading)}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`btn ${statusConfirm.tone === "danger" ? "btn-danger" : "btn-primary"}`}
                onClick={confirmStatusAction}
                disabled={Boolean(statusActionLoading)}
              >
                {statusActionLoading ? statusConfirm.loadingLabel : statusConfirm.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {documentPreview ? (
        <div
          className="verification-preview-overlay"
          onClick={() => setDocumentPreview(null)}
        >
          <div
            className="verification-preview-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="verification-preview-modal__header">
              <div>
                <span className="muted">Document Preview</span>
                <h3 className="admin-section-title" style={{ margin: "8px 0 0" }}>
                  {documentPreview.title}
                </h3>
              </div>
              <button
                type="button"
                className="btn-close"
                onClick={() => setDocumentPreview(null)}
              >
                Close
              </button>
            </div>

            <div className="verification-preview-modal__body">
              {documentPreview.images?.[documentPreview.activeSide] ? (
                <button
                  type="button"
                  className="btn"
                  onClick={() =>
                    window.open(
                      documentPreview.images?.[documentPreview.activeSide],
                      "_blank",
                      "noopener,noreferrer"
                    )
                  }
                >
                  Open in new tab
                </button>
              ) : null}
              <button
                type="button"
                className="verification-preview-modal__imageButton"
                onClick={flipDocumentPreview}
                disabled={
                  !documentPreview.images?.front || !documentPreview.images?.back
                }
                title={
                  documentPreview.images?.front && documentPreview.images?.back
                    ? "Click to flip front/back"
                    : "Only one side is available"
                }
              >
                <img
                  src={documentPreview.images?.[documentPreview.activeSide]}
                  alt={`${documentPreview.title} ${documentPreview.activeSide} side preview`}
                />
                <span className="verification-preview-modal__sideBadge">
                  {documentPreview.images?.back
                    ? documentPreview.activeSide === "front"
                      ? "Front Side"
                      : "Back Side"
                    : documentPreview.title}
                </span>
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {deactivateModal && (
        <div
          className="modal-overlay"
          onClick={() => !deactivateModal.submitting && setDeactivateModal(null)}
        >
          <div
            className="modal-box modal-box--sm"
            role="dialog"
            aria-modal="true"
            aria-labelledby="deactivate-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <div>
                <h3 id="deactivate-modal-title" className="admin-section-title" style={{ margin: 0 }}>
                  Deactivate this account?
                </h3>
                <span className="muted">
                  Their bookings, verification records, and history will be kept.
                </span>
              </div>
              <button
                type="button"
                className="btn-close"
                onClick={() => setDeactivateModal(null)}
                disabled={deactivateModal.submitting}
              >
                Close
              </button>
            </div>

            <div className="field-group">
              <div className="admin-card muted" style={{ margin: "0 0 12px" }}>
                <div className="cell-title">{getUserDisplayName(deactivateModal.user)}</div>
                <div className="cell-sub">{deactivateModal.user.email || "No Email"}</div>
              </div>

              <p className="muted" style={{ marginTop: 0, marginBottom: 8 }}>
                This account will no longer be able to sign in, but their bookings,
                verification records, and history will be kept intact.
              </p>

              <label className="field-label" htmlFor="deactivate-reason">
                Reason <span style={{ color: "#dc2626" }}>*</span>
              </label>
              <select
                id="deactivate-reason-preset"
                className="select"
                style={{ marginBottom: 6 }}
                value=""
                onChange={(e) => {
                  if (e.target.value) {
                    setDeactivateModal((prev) => ({ ...prev, reason: e.target.value, error: "" }));
                  }
                }}
                disabled={deactivateModal.submitting}
              >
                <option value="">— Select a suggested reason —</option>
                <option value="Customer requested deactivation">Customer requested deactivation</option>
                <option value="Duplicate or test account">Duplicate or test account</option>
                <option value="Policy violation">Policy violation</option>
                <option value="Staff no longer active">Staff no longer active</option>
                <option value="Administrative cleanup">Administrative cleanup</option>
                <option value="Other">Other</option>
              </select>
              <input
                id="deactivate-reason"
                className="input"
                placeholder="Enter reason for deactivation."
                value={deactivateModal.reason}
                disabled={deactivateModal.submitting}
                onChange={(e) =>
                  setDeactivateModal((prev) => ({ ...prev, reason: e.target.value, error: "" }))
                }
              />

              {deactivateModal.error ? (
                <span className="field-error">{deactivateModal.error}</span>
              ) : null}
            </div>

            <div className="btn-row" style={{ marginTop: 16 }}>
              <button
                type="button"
                className="btn"
                onClick={() => setDeactivateModal(null)}
                disabled={deactivateModal.submitting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={confirmDeactivate}
                disabled={deactivateModal.submitting || !String(deactivateModal.reason || "").trim()}
              >
                {deactivateModal.submitting ? "Deactivating…" : "Deactivate Account"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showEditModal && (
        <div className="modal-overlay" onClick={closeEditModal}>
          <div className="modal-box" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="admin-section-title" style={{ margin: 0 }}>
                {editingOwnAccount
                  ? "Edit My Profile"
                  : editingAdminAccount
                  ? "Edit Admin Account"
                  : "Edit Customer"}
              </h3>

              <button type="button" className="btn-close" onClick={closeEditModal}>
                Close
              </button>
            </div>

            <form onSubmit={saveEdit}>
              <div className="form-grid">
                <div className="field-group">
                  <label className="field-label">Full Name</label>
                  <input
                    className={`input ${editErrors.name ? "input-error" : ""}`}
                    value={editForm.name}
                    onChange={(e) =>
                      setEditForm({ ...editForm, name: e.target.value })
                    }
                  />
                  {editErrors.name && (
                    <span className="field-error">{editErrors.name}</span>
                  )}
                </div>

                {editingOwnAccount ? (
                  <div className="field-group">
                    <label className="field-label">Username</label>
                    <input
                      className={`input ${editErrors.username ? "input-error" : ""}`}
                      value={editForm.username}
                      onChange={(e) =>
                        setEditForm({ ...editForm, username: e.target.value })
                      }
                    />
                    <span className="muted">
                      Used for display and admin account identification.
                    </span>
                    {editErrors.username && (
                      <span className="field-error">{editErrors.username}</span>
                    )}
                  </div>
                ) : null}

                <div className="field-group">
                  <label className="field-label">Email</label>
                  <input
                    className={`input ${editErrors.email ? "input-error" : ""}`}
                    value={editForm.email}
                    disabled={editingOwnAccount}
                    onChange={(e) =>
                      setEditForm({ ...editForm, email: e.target.value })
                    }
                  />
                  {editingOwnAccount ? (
                    <span className="muted">Email changes use the secure email-change flow and are not editable here.</span>
                  ) : null}
                  {editErrors.email && (
                    <span className="field-error">{editErrors.email}</span>
                  )}
                </div>

                {editingAdminAccount && !editingOwnAccount ? (
                  <div className="field-group">
                    <label className="field-label">Role</label>
                    <select
                      className="select"
                      value={editForm.role}
                      onChange={(e) =>
                        setEditForm({ ...editForm, role: e.target.value })
                      }
                    >
                      {ADMIN_ROLE_OPTIONS.map((option) => (
                        <option
                          key={option.value}
                          value={option.value}
                          disabled={option.value === "technical_admin" && !isTechnicalAdmin}
                        >
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : editingOwnAccount ? (
                  <div className="field-group">
                    <label className="field-label">Role</label>
                    <span className="user-role-pill">{getRoleLabel(editingUser?.role)}</span>
                    <span className="muted">Your role and permissions cannot be changed from profile edit.</span>
                  </div>
                ) : null}
              </div>

              <div className="btn-row" style={{ marginTop: 16 }}>
                <button type="submit" className="btn btn-primary">
                  Save Changes
                </button>
                <button type="button" className="btn" onClick={closeEditModal}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminShell>
  );
}
